import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@pilot-hub/api-client'
import { Download, ExternalLink, ShieldAlert, ShieldCheck, ShieldQuestion, ShieldX } from 'lucide-react'

// Risikobewertung (EGRED 2) in der Einsatzansicht: ob und wann bewertet wurde, Anzeige/Download als PDF.
// Gleiche Komponente in Führung und Admin (apps/{fuehrung,admin}/src/components/EinsatzRisikobewertung.tsx) – Änderungen in beiden.

interface Bewertung {
  id: number; erstellt_am: string | null; erstellt_von: string | null; zweite_person: string | null
  ampel: 'gruen' | 'gelb' | 'rot' | null; titel: string | null; drohne: string | null
}

const AMPEL = {
  gruen: { icon: ShieldCheck, farbe: 'text-emerald-400', wort: 'Flug möglich' },
  gelb: { icon: ShieldAlert, farbe: 'text-amber-400', wort: 'Vor dem Flug erfüllen' },
  rot: { icon: ShieldX, farbe: 'text-red-400', wort: 'Kein Standardfall' },
} as const

const fmtDT = (s: string | null) => (s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–')
const knopf = 'inline-flex h-9 items-center gap-1.5 rounded-lg border border-dark-border px-2.5 text-xs text-dark-text-muted hover:border-primary-500/60 hover:text-dark-text disabled:opacity-50'

export function EinsatzRisikobewertung({ einsatzId, listenPfad, neuLink }: {
  einsatzId: number
  /** liefert die Bewertungen des Einsatzes, neueste zuerst */
  listenPfad: string
  /** Link zur Erfassung (nur im Führungs-Modul) */
  neuLink?: string
}) {
  const [liste, setListe] = useState<Bewertung[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<number | null>(null)

  useEffect(() => {
    let abbruch = false
    setListe(null); setFehler(null)
    api.get(listenPfad)
      .then(r => { if (!abbruch) setListe((r.data as Bewertung[]) ?? []) })
      .catch(() => { if (!abbruch) { setListe([]); setFehler('Risikobewertungen konnten nicht geladen werden.') } })
    return () => { abbruch = true }
  }, [einsatzId, listenPfad])

  /** PDF holen; anzeigen = im neuen Tab öffnen (Fenster vorab öffnen, sonst blockt Safari/iPad), sonst herunterladen */
  async function pdf(id: number, anzeigen: boolean) {
    const fenster = anzeigen ? window.open('', '_blank') : null
    setLaeuft(id); setFehler(null)
    try {
      const res = await api.get(`/risikobewertungen/${id}/pdf`, { responseType: 'blob' })
      const name = res.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/)?.[1] || `Risikobewertung_${id}.pdf`
      const url = URL.createObjectURL(new Blob([res.data as Blob], { type: 'application/pdf' }))
      if (fenster) {
        fenster.location.href = url
      } else {
        const a = document.createElement('a')
        a.href = url; a.download = name
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e: any) {
      fenster?.close()
      const data = e?.response?.data
      let text = 'PDF konnte nicht erstellt werden'
      if (data instanceof Blob) { try { text = JSON.parse(await data.text())?.detail || text } catch { /* Text bleibt */ } }
      setFehler(text)
    } finally {
      setLaeuft(null)
    }
  }

  const aktuell = liste?.[0]
  const KopfIcon = aktuell ? AMPEL[aktuell.ampel ?? 'gelb'].icon : ShieldQuestion

  return (
    <div className="pt-4 border-t border-dark-border">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-dark-text">
        <KopfIcon className={`h-4 w-4 ${aktuell ? AMPEL[aktuell.ampel ?? 'gelb'].farbe : 'text-dark-text-subtle'}`} /> Risikobewertung (EGRED 2)
      </h3>
      {liste === null ? (
        <p className="mt-2 text-xs text-dark-text-muted">Lade Risikobewertung…</p>
      ) : !aktuell ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="text-sm text-amber-300">Für diesen Einsatz wurde noch keine Risikobewertung durchgeführt.</p>
          {neuLink
            ? <Link to={neuLink} className={knopf}>Risikobewertung durchführen</Link>
            : <span className="text-xs text-dark-text-subtle">Die Bewertung erfolgt im Führungs-Modul.</span>}
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm text-dark-text">
            Durchgeführt am <b>{fmtDT(aktuell.erstellt_am)}</b> von {aktuell.erstellt_von ?? '–'}
            {aktuell.zweite_person ? `, bestätigt von ${aktuell.zweite_person}` : ''}
          </p>
          <ul className="mt-2 divide-y divide-dark-border overflow-hidden rounded-xl border border-dark-border">
            {liste!.map((b, i) => {
              const a = AMPEL[b.ampel ?? 'gelb']
              const Icon = a.icon
              return (
                <li key={b.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <Icon className={`h-4 w-4 shrink-0 ${a.farbe}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-dark-text">
                      {b.titel ?? a.wort}
                      {i === 0 && liste!.length > 1 && <span className="ml-2 rounded bg-primary-600/20 px-1.5 py-0.5 text-[10px] text-primary-200">aktuell</span>}
                    </div>
                    <div className="truncate text-xs text-dark-text-subtle">
                      {a.wort} · {fmtDT(b.erstellt_am)} · {b.erstellt_von ?? '–'}{b.drohne ? ` · ${b.drohne}` : ''}
                    </div>
                  </div>
                  <button type="button" className={knopf} disabled={laeuft === b.id} onClick={() => pdf(b.id, true)} title="Bewertung als PDF in neuem Tab anzeigen">
                    <ExternalLink className="h-3.5 w-3.5" />Anzeigen
                  </button>
                  <button type="button" className={knopf} disabled={laeuft === b.id} onClick={() => pdf(b.id, false)} title="Bewertung als PDF herunterladen">
                    <Download className="h-3.5 w-3.5" />PDF
                  </button>
                </li>
              )
            })}
          </ul>
          {neuLink && <Link to={neuLink} className="mt-2 inline-block text-xs text-primary-300 hover:underline">Neue Bewertung durchführen</Link>}
        </>
      )}
      {fehler && <p className="mt-2 text-xs text-red-300">{fehler}</p>}
    </div>
  )
}
